import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-536c5ef8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "announcement-modal-content" }
const _hoisted_2 = { class: "carousal-container" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "navigation-buttons" }
const _hoisted_5 = { class: "current-announcement" }

import { computed, inject, onMounted, ref, watch } from "vue";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { useCurrentUserStore } from "@/stores/CurrentUserStore";
import { CurrentUser } from "@/types/user/CurrentUser";
import { AnnouncementResponse } from "@/enums/announcements/AnnouncementResponse";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { HomepageAnnouncement } from "@/types/announcements/home-page-announcement";
import DxGallery from "devextreme-vue/gallery";
import { DxButton } from "devextreme-vue";
import HtmlPreview from "../ui/emails/html-preview.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";


export default /*#__PURE__*/_defineComponent({
  __name: 'announcement-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The id of the announcement clicked in the home page carousel.
   * This ensures the clicked announcement is displayed first in the modal.
   * @type {boolean}
   * @required
   */
  clickedAnnouncementId: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes.
   * This is used to update the v-model variable in the parent component and control the visibility of the modal.
   * @event update:modelValue
   * @property {boolean} value - The updated state to reflect whether the modal should be shown or hidden.
   */
  "update:modelValue",
  /**
   * Triggered when the modal is closed.
   * This is informs the announcement card component as to whether or not it shoulf refresh the carousel data.
   * @event update:modelValue
   * @property {boolean} value - The updated state to reflect whether the data should be updated or not.
   */
  "modalClosed",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const currentUserStore = useCurrentUserStore();
const currentUser = ref<CurrentUser>();
const announcements = ref<HomepageAnnouncement[]>([]);
const displayedAnnouncementIndex = ref(0);
const displayedAnnouncement = computed(() => {
  return announcements.value.length > 0
    ? announcements.value[displayedAnnouncementIndex.value]
    : null;
});
const responseProvided = ref(false);
const displayedAnnouncementPosition = computed(() => {
  return announcements.value.length > 0
    ? `${displayedAnnouncementIndex.value + 1}/${announcements.value.length}`
    : "0/0";
});
const dataLoading = ref(false);

// This code handles the fetching of the announcements to be displayed.
onMounted(async () => {
  currentUser.value = await currentUserStore.getCurrentUserDetails();
});

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      announcements.value = [];
      dataLoading.value = false;
      return;
    }

    responseProvided.value = false;
    fetchAnnouncements();
  },
);

const fetchAnnouncements = async () => {
  dataLoading.value = true;
  displayedAnnouncementIndex.value = 0; // Reset the selected position when fetching announcements
  try {
    announcements.value = await apiClient.httpGet<HomepageAnnouncement[]>(
      `app/user/announcements/${currentUser.value?.id}`,
    );
  } catch (error) {
    displayErrorMessage(getErrorMessage(error));
    announcements.value = [];
  } finally {
    // If the clicked announcement still exists, it should be displayed first.
    const clickedItemExists = announcements.value.some(
      (a) => a.id === props.clickedAnnouncementId,
    );
    if (announcements.value.length > 0 && clickedItemExists) {
      announcements.value.sort((a, b) => {
        if (a.id === props.clickedAnnouncementId) return -1;
        if (b.id === props.clickedAnnouncementId) return 1;
        return 0;
      });
    }
    dataLoading.value = false;
  }
};

// This code register peoples' interest or disinterest in the displayed announcement.
const interestLevelExpressed = async (response: AnnouncementResponse) => {
  if (!(await registerAnnouncementResponse(response))) {
    return;
  }

  if (response === AnnouncementResponse.Interested) {
    window.open(displayedAnnouncement.value?.actionUrl);
  }

  await fetchAnnouncements();
  responseProvided.value = true;
};

const registerAnnouncementResponse = async (response: AnnouncementResponse) => {
  try {
    await apiClient.httpPostVoid(
      `app/user/announcements/${currentUser.value?.id}`,
      {
        AnnouncementId: displayedAnnouncement.value?.id ?? 0,
        Response: response,
      },
    );
    return true;
  } catch (error) {
    displayErrorMessage(getErrorMessage(error));
    return false;
  }
};

// If the user has either dismissed or expressed interest in each announcement, we must close the modal as it's empty.
watch(
  () => announcements.value,
  (value, oldValue) => {
    if (oldValue.length > 0 && value.length === 0) {
      showModal.value = false;
    }
  },
  { deep: true },
);

// The modal title is based on the displayed announcement.
const modalTitle = computed(() => {
  return announcements.value.length > 0
    ? announcements.value[displayedAnnouncementIndex.value].subject
    : "";
});

// This code handles the closing of the modal.
const onCancelClick = () => (showModal.value = false);

const modalClosed = () => {
  emit("modalClosed", responseProvided.value);
};

// Function that allows user to navigate through the announcements displayed in the modal.
const switchDisplayedAnnouncements = (direction: number) => {
  // If the first item is being displayed and the back button is pressed, the last item must be displayed.
  if (displayedAnnouncementIndex.value == 0 && direction == -1) {
    displayedAnnouncementIndex.value = announcements.value.length - 1;
    return;
  }

  // If the final item is being displayed and the forward button is pressed, the first item must be displayed.
  if (
    displayedAnnouncementIndex.value == announcements.value.length - 1 &&
    direction == 1
  ) {
    displayedAnnouncementIndex.value = 0;
    return;
  }

  displayedAnnouncementIndex.value =
    displayedAnnouncementIndex.value + direction;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalBase, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
    "loading-content": dataLoading.value,
    title: modalTitle.value,
    height: "850px",
    onModalClosed: modalClosed,
    width: _unref(ModalWidth).Large
  }, {
    "non-scrollable-content": _withCtx(() => [
      _createElementVNode("section", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(DxGallery), {
            id: "gallery",
            dataSource: announcements.value,
            loop: false,
            "show-nav-buttons": false,
            "show-indicator": false,
            swipeEnabled: false,
            width: "100%",
            height: "100%",
            "item-template": "item",
            selectedIndex: displayedAnnouncementIndex.value
          }, {
            item: _withCtx(({ data }) => [
              (data.body)
                ? (_openBlock(), _createBlock(HtmlPreview, {
                    key: 0,
                    value: data.body,
                    height: "100%"
                  }, null, 8, ["value"]))
                : (_openBlock(), _createElementBlock("iframe", {
                    key: 1,
                    src: data.webPage,
                    height: "100%",
                    width: "100%",
                    frameborder: "0"
                  }, null, 8, _hoisted_3))
            ]),
            _: 1
          }, 8, ["dataSource", "selectedIndex"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(DxButton), {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (switchDisplayedAnnouncements(-1))),
            text: "",
            icon: "chevronleft"
          }),
          _createElementVNode("p", _hoisted_5, _toDisplayString(displayedAnnouncementPosition.value), 1),
          _createVNode(_unref(DxButton), {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (switchDisplayedAnnouncements(1))),
            text: "",
            icon: "chevronright"
          })
        ])
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Interested',
          onClick: () => {
            interestLevelExpressed(_unref(AnnouncementResponse).Interested);
          },
          visible: displayedAnnouncement.value?.actionUrl,
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Dismiss',
          onClick: () => {
            interestLevelExpressed(_unref(AnnouncementResponse).NotInterested);
          },
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          onClick: () => {
            onCancelClick();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "loading-content", "title", "width"]))
}
}

})